/**
 * La vue eclatee : de combien chaque corps s'ecarte pour qu'on voie la decoupe.
 *
 * Ce module est separe de `scene.ts` pour la meme raison que `describe.ts` l'est
 * du worker : `Scene` construit un `WebGLRenderer` des son constructeur et ne
 * s'instancie donc pas hors navigateur. Ce qui est ici se verifie par
 * `make verif`.
 *
 * C'est un calcul de VUE. Rien de ce qu'il produit ne touche au document ni a
 * l'export : ce qui descend en STL reste la piece a sa place.
 */

export interface Encombrement {
  min: [number, number, number];
  max: [number, number, number];
}

export type Point = [number, number, number];

/**
 * Le centre d'un ensemble d'encombrements.
 *
 * Pris sur l'encombrement global et non sur la moyenne des corps : un debit de
 * six morceaux plus un petit tourillon verrait sinon son centre tire par le
 * tourillon, et toute la piece partirait de travers.
 */
export function centreDe(boites: readonly Encombrement[]): Point {
  if (boites.length === 0) return [0, 0, 0];
  const min: Point = [Infinity, Infinity, Infinity];
  const max: Point = [-Infinity, -Infinity, -Infinity];
  for (const boite of boites) {
    for (let axe = 0; axe < 3; axe += 1) {
      min[axe] = Math.min(min[axe]!, boite.min[axe]!);
      max[axe] = Math.max(max[axe]!, boite.max[axe]!);
    }
  }
  return [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
}

/**
 * De combien chaque corps s'ecarte, a facteur 1. L'ecart est **proportionnel**
 * a la distance au centre commun, et non d'une longueur fixe : chaque morceau
 * s'eloigne dans la direction ou il etait deja, et l'arrangement de la piece
 * reste lisible au lieu de se disperser en etoile.
 *
 * Un corps pile au centre ne bouge pas, ce qui est juste : il est le pivot des
 * autres. Un corps seul ne bouge donc jamais.
 */
export function ecartements(boites: readonly Encombrement[]): Point[] {
  const centre = centreDe(boites);
  return boites.map((boite) => {
    const propre = centreDe([boite]);
    return [propre[0] - centre[0], propre[1] - centre[1], propre[2] - centre[2]];
  });
}

/** L'encombrement d'un corps une fois ecarte. Sert a verifier qu'il se degage. */
export function deplacer(boite: Encombrement, ecart: Point, facteur: number): Encombrement {
  const bouge = (p: Point): Point => [
    p[0] + ecart[0] * facteur,
    p[1] + ecart[1] * facteur,
    p[2] + ecart[2] * facteur,
  ];
  return { min: bouge(boite.min), max: bouge(boite.max) };
}

/**
 * L'espace libre entre deux encombrements, nul s'ils se touchent ou s'imbriquent.
 *
 * Les cellules d'une grille sont disjointes : deux morceaux voisins se TOUCHENT
 * sans se recouvrir. C'est donc cette distance, et non un test de recouvrement,
 * qui dit si la vue eclatee degage reellement la decoupe.
 */
export function ecartEntre(a: Encombrement, b: Encombrement): number {
  let carre = 0;
  for (let axe = 0; axe < 3; axe += 1) {
    const jeu = Math.max(b.min[axe]! - a.max[axe]!, a.min[axe]! - b.max[axe]!, 0);
    carre += jeu * jeu;
  }
  return Math.sqrt(carre);
}
